import React from "react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { AlertCircle, Calendar, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { buttonVariants } from "@/components/ui/button";
import { useSubscription } from "@/hooks/use-subscription";
import { useSession } from "@/hooks/use-session";
import { FeatureTooltip } from "@/components/FeatureTooltip";

export const CalendarSubscriptionAlert = () => {
  const { session } = useSession();
  const { isSubscribed, isTrialing, calendarDaysLimit } = useSubscription();
  const { openPricingModal } = useSubscription();
  // Only show for free users
  if (isSubscribed || isTrialing || !calendarDaysLimit || calendarDaysLimit === Infinity) {
    return null;
  }

  // For calendar alerts, we need to check if they have date range restrictions
  // This would need to be provided by the subscription context or calculated here
  // For now, we'll assume free users have restrictions
  const allowedDateRange = {
    startDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
    endDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000) // 2 days from now
  };

  // Format dates for display
  const startDateFormatted = format(allowedDateRange.startDate, 'MMM d');
  const endDateFormatted = format(allowedDateRange.endDate, 'MMM d');

  return (
    <div className={cn(
      "flex flex-col sm:flex-row sm:items-center justify-between",
      "py-2 px-4 mb-4 border border-white/10 rounded-lg",
      "bg-gray-900/50 backdrop-blur-sm"
    )}>
      <div className="flex items-center space-x-2 mb-2 sm:mb-0">
        <Calendar className="h-4 w-4 text-white/60" />
        <span className="text-sm text-white/70">Free plan limited view: </span>
        <Badge variant="outline" className="text-xs">
          {startDateFormatted} - {endDateFormatted}
        </Badge>
        <FeatureTooltip
          id="calendar-limits"
          content="Free users can only view calendar events for the current week. Premium subscribers get unlimited access to all historical and future economic events, plus advanced volatility analytics."
          position="bottom"
          delay={1000}
          className=""
        >
          <Info className="h-3.5 w-3.5 text-white/60 cursor-help" />
        </FeatureTooltip>
      </div>
      <button
        onClick={() => openPricingModal('calendar_limits')}
        className={cn(
          buttonVariants({ variant: "outline", size: "sm" }),
          "text-xs"
        )}
      >
        Upgrade to Premium
      </button>
    </div>
  );
}; 